// src/pages/ChatPage.tsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { SimBanner } from '../components/SimBanner';
import { Sidebar } from '../components/Sidebar';
import { MessageBubble } from '../components/MessageBubble';
import { useChat } from '../hooks/useChat';
import type { Persona, User } from '../types';
import styles from './ChatPage.module.css';

interface Props {
  persona: Persona;
  user: User;
  onSwitchPersona: () => void;
  onLogout: () => void;
}

const STARTERS = [
  'Who are you?',
  'What is your greatest achievement?',
  'Do you believe in God?',
  'What advice would you give to a young scientist?',
];

export function ChatPage({ persona, user, onSwitchPersona, onLogout }: Props) {
  const {
    messages, conversations, activeConvId, isLoading,
    loadConversations, loadConversation, removeConversation,
    newChat, sendMessage, rateFeedback,
  } = useChat(persona.id);

  const [input, setInput] = useState('');
  const endRef      = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Typewriter cursor on persona name — the signature element
  const [cursor, setCursor] = useState(true);
  useEffect(() => {
    if (!isLoading) {
      const t = setInterval(() => setCursor(v => !v), 600);
      return () => clearInterval(t);
    } else {
      setCursor(true);
    }
  }, [isLoading]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim());
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [input, isLoading, sendMessage]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const autoResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <div className={styles.layout}>
      <SimBanner />
      <div className={styles.app}>
        <Sidebar
          persona={persona}
          user={user}
          conversations={conversations}
          activeConvId={activeConvId}
          onNewChat={newChat}
          onLoadConv={loadConversation}
          onDeleteConv={removeConversation}
          onSwitchPersona={onSwitchPersona}
          onLogout={onLogout}
          onMounted={loadConversations}
        />

        <main className={styles.main}>
          {/* Chat header with typewriter cursor */}
          <header className={styles.header}>
            <div className={styles.headerAvatar}>{persona.avatar_letter}</div>
            <div>
              <div className={styles.headerName}>
                {persona.display_name}
                <span className={`${styles.cursor} ${cursor ? styles.cursorOn : ''}`}>_</span>
              </div>
              <div className={styles.headerSub}>
                {isLoading ? 'generating response...' : 'ready · LangGraph + Qdrant + Redis'}
              </div>
            </div>
          </header>

          {/* Messages */}
          <div className={styles.messages}>
            {messages.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>[ {persona.avatar_letter} ]</div>
                <div className={styles.emptyTitle}>
                  {persona.display_name} is ready.
                </div>
                <div className={styles.emptySub}>
                  Start a conversation. Every response is grounded in real source data
                  from this persona's documented life and work.
                </div>
                <div className={styles.chips}>
                  {STARTERS.map(s => (
                    <button key={s} className={styles.chip} onClick={() => sendMessage(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map(msg => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    onRate={rateFeedback}
                  />
                ))}
                {isLoading && (
                  <div className={styles.typing}>
                    <div className={styles.typingAvatar}>{persona.avatar_letter}</div>
                    <div className={styles.typingBubble}>
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className={styles.inputBar}>
            <div className={`${styles.inputWrap} ${input ? styles.inputFocused : ''}`}>
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={input}
                onChange={autoResize}
                onKeyDown={handleKey}
                placeholder={`Message ${persona.display_name}…`}
                rows={1}
                disabled={isLoading}
              />
            </div>
            <button
              className={styles.sendBtn}
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
